import React, { useEffect, useState } from "react";
import "../components/styles/SideBar.css";
import logo from "../assets/images/TCUK-Logo.png";
import {
  MdOutlineAccountBalance,
  MdOutlineAssignmentInd,
  MdOutlineLogin,
  MdOutlineSort,
  MdOutlineSupervisedUserCircle,
  MdOutlineSupervisorAccount,
  MdOutlineStorefront,
  MdProductionQuantityLimits,
  MdOutlineCreditCard,
} from "react-icons/md";
import { FaRegUserCircle } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa6";
import { IoMdTimer } from "react-icons/io";
import { TbUserSquare } from "react-icons/tb";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../services/api";
import { TbCategory } from "react-icons/tb";
import { AiOutlineProduct } from "react-icons/ai";
import { IoMdGift } from "react-icons/io";
import { MdOutlineFactCheck } from "react-icons/md";



function SideBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleResize = () => {
    const mobile = window.innerWidth <= 480;
    setIsMobile(mobile);

    // On mobile, collapsed by default
    // On desktop, collapsed by default (hover will expand it)
    if (!mobile) {
      setIsOpen(false); // Start collapsed on desktop
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Hover handlers for desktop
  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsOpen(false);
    }
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setIsOpen(!isOpen);
    }
  };

  const menuItems = [
    { name: "Dashboard", icon: <IoMdTimer />, path: "/dashboard" },
    {
      name: "TCUK MEMBERS",
      icon: <MdOutlineSupervisorAccount />,
      path: "/manage-member",
    },
    {
      name: "Customers",
      icon: <MdOutlineSupervisedUserCircle />,
      path: "/manage-customer",
    },
    {
      name: "Customer Ads Agents",
      icon: <MdOutlineAssignmentInd />,
      path: "/manage-advertising-agent",
    },
    {
      name: "Retail Businesses",
      icon: <MdOutlineAccountBalance />,
      path: "/manage-retailer",
    },
    {
      name: "Business Ads Agents",
      icon: <TbUserSquare />,
      path: "/manage-agent",
    },
    {
      name: "Supplier",
      icon: <MdOutlineStorefront />,
      path: "/manage-supplier",
    },
    {
      name: "Product",
      icon: <MdProductionQuantityLimits />,
      path: "/manage-product",
    },
    { 
      name: "Category", 
      icon: <TbCategory />, 
      path: "/manage-category" 
    },
    { 
      name: "Promotion", 
      icon: <IoMdGift />, 
      path: "/manage-promotion" 
    },
    {
      name: "Captain TC Gold Cards",
      icon: <MdOutlineCreditCard />,
      path: "/manage-captain-tc-cards",
    },
    {
      name: "Parental Consent",
      icon: <MdOutlineFactCheck />,
      path: "/manage-parental-consent",
    },
    {
      name: "Account Admin",
      icon: <FaRegUserCircle />,
      path: "/manage-account-admin",
    },
    {
      name: "Senior Agent Groups",
      icon: <MdOutlineSupervisorAccount />,
      path: "/manage-senior-agent-groups",
    },
    // {
    //   name: "IT Portal",
    //   icon: <FaRegUserCircle />,
    //   path: "/it-portal",
    // },
    { name: "TCUK Accounts", 
      icon: <FaRegUserCircle />, 
      path: "" 
    },
    { name: "Logout", 
      icon: <MdOutlineLogin />, 
      path: "/logout" 
    },
  ];

  const handleMenuClick = async (item) => {
    if (item.path === "/logout") {
      setShowLogoutModal(true);
    } else if (item.path) {
      navigate(item.path);
      // Always close sidebar after clicking a menu item
      setIsOpen(false);
    }
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await ApiService.logout();
      ApiService.clearToken();
      localStorage.removeItem("user_data");

      setShowLogoutModal(false);
      setIsLoggingOut(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      ApiService.clearToken();
      localStorage.removeItem("user_data");
      setShowLogoutModal(false);
      setIsLoggingOut(false);
      navigate("/");
    }
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <>
      {/* Full-screen logout confirmation modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to log out?</p>
            <div className="modal-buttons">
              <button
                className="btn-confirm"
                onClick={confirmLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging out..." : "Yes, Logout"}
              </button>
              <button
                className="btn-cancel"
                onClick={cancelLogout}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`sidebar ${isOpen ? "open" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ul className="menu">
          {/* Logo Section */}
          <li className="logo-section">
            <img src={logo} alt="logo" className="logo" />
            {/* Show title only if sidebar is open */}
            {isOpen && <span className="sidebar-title">TCUK SADMIN</span>}
          </li>

          {/* Toggle Button - Only shown on mobile */}
          {isMobile && (
            <li className="toggle-button" onClick={toggleSidebar}>
              <MdOutlineSort className="menu-icon" />
            </li>
          )}

          {/* Menu Items */}
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li
                key={item.name}
                className={`menu-item ${isActive ? "active" : ""}`}
                onClick={() => handleMenuClick(item)}
                title={!isOpen ? item.name : ""} // Show tooltip when collapsed
              >
                <div
                  className={`icon-wrapper ${isActive ? "active-icon" : ""}`}
                >
                  {item.icon}
                </div>
                {isOpen && (
                  <>
                    <span className="menu-text">{item.name}</span>
                    {isActive && <FaChevronRight className="arrow-icon" />}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

export default SideBar;
